import type { Filter, ITableProps } from "@similarweb/pro-logic-components";
import type { TableNoDataTypes } from "@similarweb/pro-logic-components/dist/table/utils/types";

import type { IComponent, ISwSettings } from "app/@types/ISwSettings";
import { isAvailable, isHidden, isLocked, isOpen } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { EErrorTypes } from "app/components/React/empty-state/GenericEmptyState";
import { EChangeStateValues } from "app/components/React/Table/cells/ChangeStateCell";
import { translate } from "app/filters/i18nFilter";
import { openHooksModalV5, openHooksModalV6 } from "app/services/ModalService";
import { EHooksVersions } from "app/services/SwHook";
import { allTrackers } from "app/services/track/track";
import { getHooksVersion } from "app/UtilitiesAndConstants/UtilityFunctions/hooksUtils";
import { isDMIAfterTrial, isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import { AI_ADS_CLAIM } from "../../app/ai-channels/constants";
import type { AdIntelligenceController } from "../shared/types/types";

export const NA_VALUE = "—";
export const PAGE_SIZE_LARGE = 100;
export const PAGE_SIZE_SMALL = 5;
export const FIRST_PAGE = 1;

const OVERVIEW_BOTTOM_WIDGETS_TABLE_TOP_HEIGHT = 140;
const OVERVIEW_BOTTOM_WIDGETS_TABLE_HEADER_HEIGHT = 32;
const OVERVIEW_BOTTOM_WIDGETS_TABLE_ROW_HEIGHT = 41;

export const OVERVIEW_BOTTOM_WIDGETS_TABLE_DIMENSIONS = {
    topHeight: OVERVIEW_BOTTOM_WIDGETS_TABLE_TOP_HEIGHT,
    headerHeight: OVERVIEW_BOTTOM_WIDGETS_TABLE_HEADER_HEIGHT,
    rowHeight: OVERVIEW_BOTTOM_WIDGETS_TABLE_ROW_HEIGHT,
    totalHeight:
        OVERVIEW_BOTTOM_WIDGETS_TABLE_TOP_HEIGHT +
        OVERVIEW_BOTTOM_WIDGETS_TABLE_HEADER_HEIGHT +
        OVERVIEW_BOTTOM_WIDGETS_TABLE_ROW_HEIGHT * 5,
} as const;

export const getChangeCellValues = ({ value, isNew }: { value: number; isNew: boolean }) => {
    const absChange = Math.abs(value);
    const precision = absChange < 0.1 ? 1 : 0;
    const hasNoData = absChange < 0.01;
    const changeState = isNew ? EChangeStateValues.New : hasNoData ? EChangeStateValues.NoData : undefined;
    return { precision, changeState };
};

export const ensureProtocol = (url: string): string => {
    if (!url) {
        return url;
    }
    if (/^https?:\/\//.test(url)) {
        return url;
    }
    return `https://${url}`;
};

export interface ISortProps {
    sort: string;
    asc: boolean;
}

export const getCategoryParam = (category?: string) => {
    return category ? { category: `$${category.replace("~", "/")}` } : {};
};

export const getGapTranslationKey = (
    controller: AdIntelligenceController,
    kind: "callout" | "empty.state",
    isMulti: boolean,
): string => {
    const prefix = controller === "publisher" ? "monetization.advertisers" : "display.ads.publishers";
    return `${prefix}.gap.toggle.${kind}${isMulti ? ".multi" : ""}`;
};

export const hasAdIntelBetaAccess = () => {
    return isAvailable(swSettings.components.AdIntMaster as IComponent);
};

export const hasSearchAndAdsCanary = (): boolean => {
    return !!swSettings.components.Home?.resources?.HasSearchAndAdsCanary;
};

export const isDailyGranularityLocked = (): boolean => {
    const component = swSettings.components.AdIntDisplay as IComponent;
    return component?.resources?.DailyDuration !== "Open";
};

export const adIntelIsOpen = (): boolean => {
    return isOpen(swSettings.components.AdIntMaster as IComponent);
};

export const adSearchIsOpen = (): boolean => {
    return isOpen((swSettings as ISwSettings).components.AdIntSearch);
};

export const hasOnlySearchAvailable = (): boolean => !adIntelIsOpen() && adSearchIsOpen();

export const isAdScreenshotsHooked = (): boolean => {
    const component = swSettings.components.AdIntDisplay as IComponent;
    return component?.resources?.AdScreenshots !== "Open";
};

export const isAdIntelligenceNoTouchPreviewVariant = (isPreview = false): boolean => {
    return isPreview && isNoTouch(swSettings) && !adIntelIsOpen();
};

export const shouldUseAdIntelligencePreviewHook = (isPreview = false): boolean => {
    if (!isPreview) {
        return false;
    }

    if (adIntelIsOpen()) {
        return false;
    }

    return !hasAdIntelBetaAccess() || isNoTouch(swSettings);
};

export const hasAdIntelligenceOverviewAccess = (isPreview = false): boolean =>
    !shouldUseAdIntelligencePreviewHook(isPreview) && hasAdIntelBetaAccess();

export const hasAdIntelligenceOverviewOpenAccess = (isPreview = false): boolean =>
    !shouldUseAdIntelligencePreviewHook(isPreview) && adIntelIsOpen();

export const getAdIntelligenceHookKey = (controller: AdIntelligenceController = "advertiser"): string => {
    if ((isNoTouch(swSettings) || isDMIAfterTrial(swSettings)) && !adIntelIsOpen()) {
        return controller === "advertiser" ? "advertiserActivityNoTouch" : "publisherAnalysisNoTouch";
    }

    return controller === "advertiser" ? "AdIntelligence" : "PublisherAnalysis";
};

export const getAdIntelligenceHookVersion = (): EHooksVersions => getHooksVersion(swSettings);

export const openAdIntelligenceHookModal = (controller: AdIntelligenceController = "advertiser"): void => {
    const key = getAdIntelligenceHookKey(controller);

    if (getAdIntelligenceHookVersion() === EHooksVersions.V6) {
        openHooksModalV6(key);
        return;
    }

    openHooksModalV5(key);
};

export const isAdIntelligenceLocked = () => {
    return isLocked(swSettings.components.AdIntMaster as IComponent) || isNoTouch(swSettings);
};

export const isAdIntelligenceNavigationLocked = (): boolean => {
    if (adIntelIsOpen()) {
        return false;
    }

    return isAdIntelligenceLocked();
};

interface IAiAdsAvailability {
    /** Nothing about AI ads is shown to this user. */
    isHidden: boolean;
    /** The report exists for this user but is gated behind an upgrade. */
    isLocked: boolean;
}

/**
 * Entitlement state of the AI ads report, so callers outside the zone — the sidebar — decide what
 * to render without knowing which claim backs it.
 */
export const getAiAdsAvailability = (): IAiAdsAvailability => {
    const claim = (swSettings as ISwSettings).components[AI_ADS_CLAIM];

    return { isHidden: isHidden(claim), isLocked: isLocked(claim) };
};

export const hasVerificationWidgetAccess = (): boolean => {
    const component = (swSettings.components as Record<string, IComponent>).AdIntDisplay as IComponent | undefined;
    // Currently this field have only on/off values, if not open all is false.
    return (component?.resources?.VerificationReport as string | undefined)?.toLowerCase() === "open";
};

export const adDownloadLimits = (): {
    value: number;
    rule: "Unlimited" | "Limited" | "Hidden";
} => {
    const component = swSettings.components.AdIntDisplay as IComponent;
    const setLimit = component?.resources?.AdDownloadCount as number | undefined;

    if (typeof setLimit !== "number" || setLimit < 0) {
        return { value: -1, rule: "Unlimited" };
    }

    return { value: setLimit, rule: setLimit === 0 ? "Hidden" : "Limited" };
};

export const obfuscateData = (value: number, change: number, hasAdIntelMetrics: boolean, visits: number) => {
    const hasAccessToAdIntelMetrics = hasAdIntelBetaAccess();

    const hasHiddenData = !hasAccessToAdIntelMetrics && hasAdIntelMetrics && !value && !!visits;

    const randomValue = Math.floor(Math.random() * 1000000);
    const randomChange = Math.floor(Math.random() * 100);
    return {
        value: hasHiddenData ? randomValue : value,
        change: hasHiddenData ? randomChange : change,
    };
};

export const trackExcelDownload = () => {
    allTrackers.dispatchTrackEvent("Download", "click", "Table/Excel");
};

export const buildSearchFilter = ({
    initialValue,
    placeholder,
    onSearchChange,
}: {
    initialValue: string;
    placeholder: string;
    onSearchChange: (value: string) => void;
}): Filter[] => [
    {
        filter: "search",
        initialValue,
        placeholder,
        onSearchChange,
    },
];

export const getNoDataObject = (
    error: boolean,
    controller: AdIntelligenceController = "advertiser",
): ITableProps["noData"] => {
    return error
        ? {
              title: translate(`ad.intelligence.${controller}.table.error.title`),
              subtitle: translate(`ad.intelligence.${controller}.table.error.subtitle`),
              type: "error",
          }
        : {
              title: translate(`ad.intelligence.${controller}.table.no.data.title`),
              subtitle: translate(`ad.intelligence.${controller}.table.no.data.subtitle`),
              type: "no-data",
          };
};

export const getErrorTypeFromTableNoDataType = (type?: TableNoDataTypes): EErrorTypes | undefined => {
    switch (type) {
        case "no-data":
            return EErrorTypes.NO_DATA;
        case "error":
            return EErrorTypes.ERROR;
        default:
            return undefined;
    }
};

/**
 * Calculate which API page to fetch based on current UI page
 *
| ui page | api page |
| ------- | -------- |
| 1-20    | 1        |
| 21-40   | 2        |
| ...     | ...      |
 */
export const calculateApiPage = (currentPage: number): number => {
    return Math.ceil((currentPage * PAGE_SIZE_SMALL) / PAGE_SIZE_LARGE);
};

/**
 * Slice the data to show only the current UI page
 * Calculates the offset within the fetched API data
 */
export const slicePaginatedData = <T>(items: T[] | undefined, currentPage: number): T[] | undefined => {
    if (!items) {
        return;
    }
    const startIndex = ((currentPage - 1) * PAGE_SIZE_SMALL) % PAGE_SIZE_LARGE;
    const endIndex = startIndex + PAGE_SIZE_SMALL;
    return items.slice(startIndex, endIndex);
};
